import React, { useState } from 'react';
import {
  Typography,
  Box,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Container,
  Button,
  Chip,
  IconButton,
  alpha,
  useTheme
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowBack } from '@mui/icons-material';
import { handlePreviewFile } from 'src/services/common';

// Interfaces for data types
interface DownloadedDocument {
  BidDocumentID: number;
  DocumentName: string;
  DownloadDate: string;
  DownloadedBy: number;
  DocumentUrl: string;
  FullName?: string;
  Email?: string;
  PhoneNumber?: string;
  BidID: number;
  ChildBidID: number;
}

interface DownloadData {
  bidID: string;
  typeID: number;
  bidType: string;
  title: string;
  category: string;
  adDate: string;
  openingDate: string;
  status: string;
  statusID: number;
  poNumber?: string;
  downloadedDocuments: DownloadedDocument[];
  categories: {
    CategoryID: number;
    CategoryName: string;
  }[];
}

interface DownloadDetailsProps {
  bidData?: {
    bidID: string;
    typeID: number;
    bidType: string;
    title: string;
    categories: {
      CategoryID: number;
      CategoryName: string;
    }[];
    adDate: string;
    openingDate: string;
    status: string;
    statusID: number;
    poNumber?: string;
    downloadedDocuments: DownloadedDocument[];
    category?: string;
  };

  setViewMode?: React.Dispatch<React.SetStateAction<'list' | 'details'>>;
  setSelectedBid?: React.Dispatch<React.SetStateAction<DownloadData | null>>;
}

// Type for the column sorting
type Order = 'asc' | 'desc';
type DocumentKeys = keyof DownloadedDocument | 'formattedDate';

const DownloadDetails: React.FC<DownloadDetailsProps> = ({
  bidData,
  setViewMode,
  setSelectedBid
}) => {
  const { state: locationState, township, uuid } = useParams();

  const navigate = useNavigate();

  if (!bidData) {
    return (
      <Container maxWidth={false} sx={{ width: '90%', mx: 'auto' }}>
        <Box sx={{ my: 4 }}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography color="error" gutterBottom>
              No bid data provided
            </Typography>
            <Button
              variant="outlined"
              onClick={() =>
                navigate(
                  `/${locationState}/${township}/${uuid}/bid-portal/downloads`
                )
              }
            >
              Return to My Downloads
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [childPage, setChildPage] = useState(0); // Pagination for child docs
  const [childRowsPerPage, setChildRowsPerPage] = useState(10); // Child rows per page
  const theme = useTheme();

  // Sorting state
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<DocumentKeys>('DownloadDate');

  // Handle sorting
  const handleRequestSort = (property: DocumentKeys) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Handle pagination
  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle child pagination
  const handleChildChangePage = (_: unknown, newPage: number) => {
    setChildPage(newPage);
  };

  const handleChildChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setChildRowsPerPage(parseInt(event.target.value, 10));
    setChildPage(0);
  };

  // Sort function
  const stableSort = <T extends object>(
    array: T[],
    comparator: (a: T, b: T) => number
  ) => {
    const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
    stabilizedThis.sort((a, b) => {
      const compareResult = comparator(a[0], b[0]);
      if (compareResult !== 0) return compareResult;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  };

  const descendingComparator = <T extends object>(
    a: T,
    b: T,
    property: keyof T
  ) => {
    if (b[property] < a[property]) {
      return -1;
    }
    if (b[property] > a[property]) {
      return 1;
    }
    return 0;
  };

  const getComparator = <Key extends keyof any>(
    sortOrder: Order,
    sortOrderBy: Key
  ): ((a: { [key in Key]: any }, b: { [key in Key]: any }) => number) => {
    return sortOrder === 'desc'
      ? (a, b) => descendingComparator(a, b, sortOrderBy)
      : (a, b) => -descendingComparator(a, b, sortOrderBy);
  };

  // Function to format download date
  const formatDownloadDate = (dateString: string) => {
    if (!dateString) return '-';

    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      }).format(date);
    } catch (e) {
      return '-';
    }
  };

  // Function to get parent and child documents
  const getParentDocuments = () => {
    return bidData?.downloadedDocuments.filter(
      (doc) => doc.BidID > 0 && doc.ChildBidID === 0
    );
  };

  const getChildDocuments = () => {
    return bidData?.downloadedDocuments.filter(
      (doc) => doc.BidID > 0 && doc.ChildBidID > 0
    );
  };

  // Return to bid list function
  const returnToBidList = () => {
    setViewMode('list');
    setSelectedBid(null);
  };

  // Categories as a string
  const getCategoriesString = () => {
    return (
      bidData?.categories?.map((cat) => cat.CategoryName).join(', ') ||
      bidData?.category ||
      ''
    );
  };

  const parentDocuments = getParentDocuments();
  const childDocuments = getChildDocuments();

  return (
    <Container maxWidth={false} sx={{ width: '100%', mx: 'auto', p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 3,
          gap: 2
        }}
      >
        <IconButton
          onClick={returnToBidList}
          sx={{
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.main,
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.2)
            }
          }}
        >
          <ArrowBack />
        </IconButton>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 'bold',
            color: theme.palette.primary.main,
            position: 'relative',
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: -8,
              left: 0,
              width: 190,
              height: 4,
              borderRadius: 2,
              backgroundColor: theme.palette.primary.main
            }
          }}
        >
          Return to My Downloads
        </Typography>
      </Box>
      <Box component="div" id="main" sx={{ my: 3 }}>
        <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', mb: 2 }}>
                <Typography
                  variant="subtitle1"
                  component="span"
                  sx={{ fontWeight: 'bold', mr: 1 }}
                >
                  Title:
                </Typography>
                <Typography variant="body1">{bidData.title}</Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', mb: 2 }}>
                <Typography
                  variant="subtitle1"
                  component="span"
                  sx={{ fontWeight: 'bold', mr: 1 }}
                >
                  Categories:
                </Typography>
                <Typography variant="body1">{getCategoriesString()}</Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', mb: 2 }}>
                <Typography
                  variant="subtitle1"
                  component="span"
                  sx={{ fontWeight: 'bold', mr: 1 }}
                >
                  Type:
                </Typography>
                <Typography variant="body1">{bidData.bidType}</Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', mb: 2 }}>
                <Typography
                  variant="subtitle1"
                  component="span"
                  sx={{ fontWeight: 'bold', mr: 1 }}
                >
                  Ad Date:
                </Typography>
                <Typography variant="body1">
                  {bidData.adDate
                    ? new Date(bidData.adDate).toLocaleDateString('en-US')
                    : ''}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', mb: 2 }}>
                <Typography
                  variant="subtitle1"
                  component="span"
                  sx={{ fontWeight: 'bold', mr: 1 }}
                >
                  Opening Date:
                </Typography>
                <Typography variant="body1">
                  {bidData.openingDate
                    ? formatDownloadDate(bidData.openingDate)
                    : ''}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', mb: 2 }}>
                <Typography
                  variant="subtitle1"
                  component="span"
                  sx={{ fontWeight: 'bold', mr: 1 }}
                >
                  Status:
                </Typography>
                <Chip
                  label={bidData.status}
                  color={
                    bidData.status.toLowerCase() === 'open'
                      ? 'success'
                      : bidData.status.toLowerCase() === 'closed'
                      ? 'warning'
                      : bidData.status.toLowerCase() === 'awarded'
                      ? 'info'
                      : 'default'
                  }
                  size="small"
                />
              </Box>
            </Grid>

            {bidData.poNumber && (
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', mb: 2 }}>
                  <Typography
                    variant="subtitle1"
                    component="span"
                    sx={{ fontWeight: 'bold', mr: 1 }}
                  >
                    PO Number:
                  </Typography>
                  <Typography variant="body1">{bidData.poNumber}</Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Parent Documents Table */}
        {parentDocuments.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Downloaded Documents
            </Typography>
            <TableContainer component={Paper}>
              <Table aria-label="downloaded documents table">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'DocumentName'}
                        direction={orderBy === 'DocumentName' ? order : 'asc'}
                        onClick={() => handleRequestSort('DocumentName')}
                      >
                        Document Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'DownloadDate'}
                        direction={orderBy === 'DownloadDate' ? order : 'asc'}
                        onClick={() => handleRequestSort('DownloadDate')}
                      >
                        Download Date & Time
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stableSort(parentDocuments, getComparator(order, orderBy))
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((doc, index) => (
                      <TableRow key={index}>
                        <TableCell>{doc.DocumentName}</TableCell>
                        <TableCell>
                          {formatDownloadDate(doc.DownloadDate)}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handlePreviewFile(doc.DocumentUrl)}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  {parentDocuments.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        <Typography
                          sx={{
                            py: 2,
                            fontStyle: 'italic',
                            color: 'text.secondary'
                          }}
                        >
                          No documents found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={parentDocuments.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableContainer>
          </Box>
        )}

        {/* Child Documents Table */}
        {childDocuments.length > 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Child Bid Downloaded Documents
            </Typography>
            <TableContainer component={Paper}>
              <Table aria-label="child downloaded documents table">
                <TableHead>
                  <TableRow>
                    <TableCell>Document Name</TableCell>
                    <TableCell>Download Date & Time</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {childDocuments
                    .slice(
                      childPage * childRowsPerPage,
                      childPage * childRowsPerPage + childRowsPerPage
                    )
                    .map((doc, index) => (
                      <TableRow key={index}>
                        <TableCell>{doc.DocumentName}</TableCell>
                        <TableCell>
                          {formatDownloadDate(doc.DownloadDate)}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handlePreviewFile(doc.DocumentUrl)}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={childDocuments.length}
                rowsPerPage={childRowsPerPage}
                page={childPage}
                onPageChange={handleChildChangePage}
                onRowsPerPageChange={handleChildChangeRowsPerPage}
              />
            </TableContainer>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default DownloadDetails;
